package fr.arcplex.sooner.webui

import fr.arcplex.network.model.CoherenceFilmInfo
import fr.arcplex.network.model.CoherenceProgramContents
import fr.arcplex.network.model.Rail
import fr.arcplex.network.model.RailItem
import fr.arcplex.sooner.coherence.CoherenceThemeState

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.runtime.withFrameNanos
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.positionInRoot
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage
import fr.arcplex.network.logging.Logger
import fr.arcplex.network.template.*
import fr.arcplex.network.remoteImageUrl

// Brand palette for Coherence views — resolved from CoherenceThemeState at runtime.
@Composable
private fun brandBg(): Color =
    parseHexColorOrNull(CoherenceThemeState.theme.page.background) ?: Color(0xFF0B0E12)

@Composable
private fun textPrimary(): Color =
    parseHexColorOrNull(CoherenceThemeState.theme.page.text) ?: Color(0xFFFFFFFF)

@Composable
private fun textMuted(): Color =
    parseHexColorOrNull(CoherenceThemeState.theme.page.muted) ?: Color(0x9EFFFFFF)

@Composable
private fun textBody(): Color =
    parseHexColorOrNull(CoherenceThemeState.theme.kinds.text.color) ?: Color(0xD1FFFFFF)

@Composable
fun CoherenceFilmInfoView(
    filmInfo: CoherenceFilmInfo,
    modifier: Modifier = Modifier,
    onActionClicked: ((String) -> Unit)? = null,
    onRailItemClicked: ((Int) -> Unit)? = null,
    isTV: Boolean = false,
    menu: Rail? = null,
    onMenuItemClicked: ((RailItem) -> Unit)? = null,
    menuOpen: Boolean = false,
    onMenuToggle: () -> Unit = {},
    onMenuDismiss: () -> Unit = {}
) {
    val contents = filmInfo.contents
    val template = filmInfo.template
    val scrollState = rememberScrollState()
    var focusedStrip by remember { mutableStateOf(-1) }

    val BrandBg = brandBg()
    val TextPrimary = textPrimary()
    val TextMuted = textMuted()
    val TextBody = textBody()

    BoxWithConstraints(modifier = modifier.fillMaxSize()) {
        val cqw = maxWidth / 100f
        val vh = maxHeight
        val heroRegion = template.regions.firstOrNull { hasFillBackdrop(it) }
        val actions =
            (heroRegion ?: template.regions.firstOrNull { regionActions(it).isNotEmpty() })
                ?.let { regionActions(it) } ?: emptyList()
        // Only actions the content actually offers are rendered/registered.
        val visibleActions = actions.filter { actionAvailable(it.name, contents) }
        val playIndex = visibleActions.indexOfFirst { it.name == "play" }
        // Non-empty rails, in render order (must match RailsRegion).
        val railNames = contents.rails.keys.filter { contents.rails[it]?.items?.isNotEmpty() == true }

        // strip ids: 0 = poster, 1 = actions, 2.. = rails.
        val railStripBase = 2

        // Standard focus plumbing: one FocusRequester per strip entry point.
        // Down/Up between strips is declared with focusProperties on each focusable.
        val posterRequester = remember { FocusRequester() }
        val actionRequesters = remember(filmInfo) { List(visibleActions.size) { FocusRequester() } }
        val railEntryRequesters = remember(filmInfo) { List(railNames.size) { FocusRequester() } }

        fun onStripFocused(stripId: Int) {
            if (focusedStrip != stripId) {
                Logger.d("COH", "focus strip=$stripId")
                focusedStrip = stripId
            }
        }

        // Content-space [top, bottom] px per strip, reported by the strips themselves after layout.
        val stripBounds = remember { mutableMapOf<Int, IntArray>() }
        fun recordStrip(stripId: Int, screenTopPx: Int, screenBottomPx: Int) {
            val scroll = scrollState.value
            val top = screenTopPx + scroll
            val bottom = screenBottomPx + scroll
            val prev = stripBounds[stripId]
            if (prev != null && prev[0] == top && prev[1] == bottom) return
            stripBounds[stripId] = intArrayOf(top, bottom)
            Logger.d("COH", "strip $stripId bounds=[top=$top bottom=$bottom] content px")
        }

        LaunchedEffect(filmInfo) {
            Logger.d(
                "COH",
                "view: vh=$vh cqw=$cqw isTV=$isTV regions=${template.regions.map { it.name }} " +
                    "heroRegion=${heroRegion?.name} actions=${actions.map { it.name }} " +
                    "visible=${visibleActions.map { it.name }} rails=${contents.rails.keys}"
            )
            snapshotFlow { scrollState.maxValue }.collect { max ->
                Logger.d("COH", "scroll: maxValue=$max contentHeight~=${max.dp + vh} viewport=$vh")
            }
        }

        // Spec: on load the play button gets focus; the poster only if there is no play button.
        LaunchedEffect(filmInfo) {
            if (!isTV) return@LaunchedEffect
            withFrameNanos {} // let the first layout attach the focus requesters
            val target = if (playIndex >= 0) actionRequesters[playIndex] else posterRequester
            val where = if (playIndex >= 0) "play action strip=1 idx=$playIndex" else "poster strip=0 (no play action)"
            val result = runCatching { target.requestFocus() }
            Logger.d("COH", "initial focus: $where ok=${result.isSuccess}")
        }

        val density = LocalDensity.current
        val vhPx = with(density) { vh.roundToPx() }
        val stripMarginPx = with(density) { 12.dp.roundToPx() }
        LaunchedEffect(focusedStrip) {
            if (focusedStrip < 0) return@LaunchedEffect
            val bounds = stripBounds[focusedStrip]
            if (bounds == null) {
                Logger.d("COH", "focus strip=$focusedStrip -> no bounds yet, no scroll")
                return@LaunchedEffect
            }
            val cur = scrollState.value
            val max = scrollState.maxValue
            // Bring the focused strip into view: keep it if fully visible, otherwise align its
            // top near the viewport top, or scroll just enough to show its bottom.
            val target = when {
                bounds[0] >= cur && bounds[1] <= cur + vhPx -> cur
                bounds[0] - stripMarginPx < cur -> (bounds[0] - stripMarginPx).coerceAtLeast(0)
                else -> (bounds[1] - vhPx + stripMarginPx).coerceIn(0, max)
            }
            Logger.d(
                "COH",
                "focus strip=$focusedStrip bounds=[${bounds[0]}, ${bounds[1]}] cur=$cur " +
                    "-> scroll $target (max=$max)"
            )
            if (target != cur) scrollState.animateScrollTo(target)
        }

        Column(
            modifier = modifier
                .fillMaxSize()
                .verticalScroll(scrollState)
                .background(BrandBg)
        ) {
            var accumulated = 0.dp
            template.regions.forEachIndexed { _, region ->
                val regionTop = accumulated
                val regionHeight = parseHeight(region.height, vh)
                when {
                    region.rails.isNotEmpty() -> {
                        Logger.d(
                            "COH",
                            "region '${region.name}' top=$regionTop h=$regionHeight branch=RAILS " +
                                "contentRails=${contents.rails.size} defs=${region.rails.map { it.name }}"
                        )
                        RailsRegion(
                            region = region,
                            contents = contents,
                            modifier = Modifier.fillMaxWidth(),
                            onRailItemClicked = onRailItemClicked,
                            isTV = isTV,
                            cqw = cqw,
                            railHeight = regionHeight,
                            railNames = railNames,
                            posterRequester = posterRequester,
                            actionRequester = actionRequesters.firstOrNull(),
                            railEntryRequesters = railEntryRequesters,
                            railStripBase = railStripBase,
                            onStripFocused = { id -> onStripFocused(id) },
                            onStripBounds = { id, top, bottom -> recordStrip(id, top, bottom) }
                        )
                        accumulated += regionHeight
                    }
                    hasFillBackdrop(region) -> {
                        Logger.d("COH", "region '${region.name}' top=$regionTop h=$regionHeight branch=HERO")
                        HeroRegion(
                            region = region,
                            contents = contents,
                            modifier = Modifier.fillMaxWidth().height(regionHeight),
                            isTV = isTV,
                            posterRequester = posterRequester,
                            actionRequesters = actionRequesters,
                            firstRailEntry = railEntryRequesters.firstOrNull(),
                            cqw = cqw,
                            onActionClicked = onActionClicked,
                            onStripFocused = { id -> onStripFocused(id) },
                            onStripBounds = { id, top, bottom -> recordStrip(id, top, bottom) }
                        )
                        accumulated += regionHeight
                    }
                    region.slots.isNotEmpty() -> {
                        Logger.d("COH", "region '${region.name}' top=$regionTop h=$regionHeight branch=DETAILS slots=${region.slots.map { it.name }}")
                        DetailsRegion(
                            region = region,
                            contents = contents,
                            modifier = Modifier.fillMaxWidth().height(regionHeight),
                            cqw = cqw
                        )
                        accumulated += regionHeight
                    }
                    else -> {
                        Logger.d("COH", "region '${region.name}' top=$regionTop h=$regionHeight branch=PLACEHOLDER")
                        TemplateRegion(
                            region = region,
                            modifier = Modifier.fillMaxWidth().height(regionHeight)
                        )
                        accumulated += regionHeight
                    }
                }
            }
            Logger.d("COH", "regions done: accumulated=$accumulated viewport=$vh strips=${stripBounds.keys.sorted()}")
            if (heroRegion == null && visibleActions.isNotEmpty()) {
                ActionsSection(
                    actions = visibleActions,
                    modifier = Modifier.fillMaxWidth(),
                    onActionClicked = onActionClicked,
                    isTV = isTV,
                    cqw = cqw,
                    posterRequester = posterRequester,
                    actionRequesters = actionRequesters,
                    firstRailEntry = railEntryRequesters.firstOrNull(),
                    onStripFocused = { id -> onStripFocused(id) },
                    onStripBounds = { id, top, bottom -> recordStrip(id, top, bottom) }
                )
                accumulated += 40.dp
            }
        }

        // Burger menu entry + panel, shared with PageView/FilmInfoView.
        val menuRail = menu
        if (menuRail != null && menuRail.items.isNotEmpty()) {
            var hamburgerFocused by remember { mutableStateOf(false) }
            Card(
                onClick = onMenuToggle,
                modifier = Modifier
                    .align(Alignment.TopStart)
                    .padding(16.dp)
                    .onFocusChanged { hamburgerFocused = it.isFocused }
                    .then(if (hamburgerFocused) Modifier.border(3.dp, Color.White) else Modifier),
                shape = RoundedCornerShape(0.dp),
                colors = CardDefaults.cardColors(
                    containerColor = Color.Transparent,
                    contentColor = TextPrimary,
                    disabledContainerColor = Color.Transparent,
                    disabledContentColor = TextPrimary
                ),
                elevation = CardDefaults.cardElevation(defaultElevation = 0.dp)
            ) {
                Text(
                    text = "☰",
                    color = TextPrimary,
                    modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp)
                )
            }

            AppMenu(
                menu = menuRail,
                isOpen = menuOpen,
                coherence = true,
                isTV = isTV,
                onDismiss = onMenuDismiss,
                onItemClicked = { onMenuItemClicked?.invoke(it) }
            )
        }
    }
}

@Composable
private fun HeroRegion(
    region: CoherenceRegion,
    contents: CoherenceProgramContents,
    modifier: Modifier = Modifier,
    isTV: Boolean = false,
    posterRequester: FocusRequester,
    actionRequesters: List<FocusRequester>,
    firstRailEntry: FocusRequester?,
    cqw: Dp = 0.dp,
    onActionClicked: ((String) -> Unit)? = null,
    onStripFocused: (Int) -> Unit = {},
    onStripBounds: (Int, Int, Int) -> Unit = { _, _, _ -> }
) {
    val heroColumn = region.layers.firstOrNull()?.columns?.firstOrNull()
    val contentGap = gapDp(heroColumn?.gap ?: "sm")

    Logger.d(
        "COH",
        "hero: isTV=$isTV colSlots=${heroColumn?.slots?.map { it.name }} " +
            "rows=${heroColumn?.rows?.map { r -> "slots=${r.slots.map { it.name }} actions=${r.actions.map { it.name }}" }} " +
            "regionSlots=${region.slots.map { it.name }}"
    )

    // focusProperties must precede the focus target (clickable) in the chain.
    // Down from the poster enters the action row (or the first rail).
    val heroModifier = modifier
        .focusRequester(posterRequester)
        .onFocusChanged { state ->
            if (state.isFocused) onStripFocused(0)
        }
        .focusProperties {
            val target = actionRequesters.firstOrNull() ?: firstRailEntry
            if (target != null) down = target
        }
        .clickable { actionRequesters.firstOrNull()?.let { runCatching { it.requestFocus() } } }
        .onGloballyPositioned { coords ->
            val top = coords.positionInRoot().y.toInt()
            onStripBounds(0, top, top + coords.size.height)
        }

    Box(modifier = heroModifier) {
        if (contents.images.backdrop.isNotBlank()) {
            AsyncImage(
                model = remoteImageUrl(contents.images.backdrop),
                contentDescription = null,
                modifier = Modifier.fillMaxSize(),
                contentScale = ContentScale.Crop
            )
        } else {
            Box(modifier = Modifier.fillMaxSize().background(brandBg()))
        }

        // Bottom-to-top legibility scrim.
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(
                    Brush.verticalGradient(
                        0.22f to Color(0x000B0E12),
                        0.45f to Color(0x400B0E12),
                        0.92f to Color(0xF50B0E12)
                    )
                )
        )

        Column(
            modifier = Modifier
                .align(Alignment.BottomStart)
                .padding(26.dp),
            verticalArrangement = Arrangement.spacedBy(contentGap)
        ) {
            // Generic: render whatever slots/rows the template declares, in order.
            // Col-level slots first, then rows (the parser keeps document order
            // within each list; no known template interleaves col slots and rows),
            // then region-level content slots (e.g. rev13 "genres").
            heroColumn?.slots?.forEach { ref ->
                HeroSlot(ref.name, ref.maxItems, 0, contents, cqw)
            }
            var actionIndex = 0
            heroColumn?.rows?.forEach { row ->
                val visible = row.actions.filter { actionAvailable(it.name, contents) }
                if (visible.isNotEmpty()) {
                    Row(
                        modifier = Modifier
                            // Actions live inside the hero: hasFocus (not isFocused) tracks
                            // focus arriving on any button of the row.
                            .onFocusChanged { state -> if (state.hasFocus) onStripFocused(1) }
                            .onGloballyPositioned { coords ->
                                val top = coords.positionInRoot().y.toInt()
                                onStripBounds(1, top, top + coords.size.height)
                            },
                        horizontalArrangement = Arrangement.spacedBy(gapDp(row.gap.ifBlank { "md" })),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        visible.forEach { action ->
                            CoherenceActionButton(
                                label = actionLabel(action.name),
                                emphasis = action.emphasis,
                                bgColorOverride = action.bgColor,
                                onClick = { onActionClicked?.invoke(action.name) },
                                isTV = isTV,
                                cqw = cqw,
                                requester = actionRequesters[actionIndex++],
                                upTarget = posterRequester,
                                downTarget = firstRailEntry
                            )
                        }
                    }
                } else if (row.slots.isNotEmpty()) {
                    MetaSlotRow(
                        refs = row.slots.map { it.name to it.maxItems },
                        contents = contents,
                        gap = gapDp(row.gap.ifBlank { "sm" }),
                        cqw = cqw
                    )
                }
            }
            region.slots.filterNot { isImageSlot(it.name) }.forEach { slot ->
                HeroSlot(slot.name, slot.maxItems, slot.maxLines, contents, cqw)
            }
        }
    }
}

@Composable
private fun DetailsRegion(
    region: CoherenceRegion,
    contents: CoherenceProgramContents,
    modifier: Modifier = Modifier,
    cqw: Dp = 0.dp
) {
    // KDL: region "details" col=1 span=9 — 9 of 12 grid columns (≈75% width),
    // with only the rbody's 8px padding as inset. Renders whatever slots the
    // template declares, in order.
    Column(
        modifier = modifier
            .fillMaxWidth(0.75f)
            .padding(8.dp)
    ) {
        region.slots.forEach { slot ->
            DetailsSlot(slot, contents, cqw)
        }
    }
}

@Composable
private fun DetailsSlot(slot: CoherenceSlot, contents: CoherenceProgramContents, cqw: Dp = 0.dp) {
    val values = slotValues(slot.name, contents)
        .take(slot.maxItems.takeIf { it > 0 } ?: Int.MAX_VALUE)
    if (values.isEmpty() || isImageSlot(slot.name)) {
        if (!isImageSlot(slot.name)) Logger.d("COH", "details: no values for slot '${slot.name}', skipped")
        return
    }
    when (slot.name) {
        "title" -> {
            HeroTitle(contents.title, cqw)
            Spacer(Modifier.height(6.dp))
        }
        "synopsis" -> {
            HeroBody(contents.synopsis, slot.maxLines.takeIf { it > 0 } ?: Int.MAX_VALUE, cqw)
            Spacer(Modifier.height(6.dp))
        }
        "genres" -> {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(6.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                values.forEach { GenreChip(it, cqw) }
            }
            Spacer(Modifier.height(6.dp))
        }
        else -> {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = "${slotLabel(slot)} : ",
                    color = textPrimary(),
                    fontSize = dpToSp(scaleDp(7, 1.1f, cqw)),
                    fontWeight = FontWeight.SemiBold
                )
                Text(
                    text = values.joinToString(", "),
                    color = textMuted(),
                    fontSize = dpToSp(scaleDp(7, 1.1f, cqw))
                )
            }
            Spacer(Modifier.height(6.dp))
        }
    }
}

// Block-level slot inside hero content: style is driven by the slot name,
// placement by the template.
@Composable
private fun HeroSlot(
    name: String,
    maxItems: Int,
    maxLines: Int,
    contents: CoherenceProgramContents,
    cqw: Dp = 0.dp
) {
    val limit = maxItems.takeIf { it > 0 } ?: Int.MAX_VALUE
    when (name) {
        "title" -> if (contents.title.isNotBlank()) HeroTitle(contents.title, cqw)
        "synopsis" -> if (contents.synopsis.isNotBlank()) {
            HeroBody(contents.synopsis, maxLines.takeIf { it > 0 } ?: 3, cqw)
        }
        "genres" -> {
            val genres = contents.genres.take(limit)
            if (genres.isNotEmpty()) {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    genres.forEach { GenreChip(it, cqw) }
                }
            }
        }
        "year", "duration" -> slotValues(name, contents).forEach { MetaText(it, cqw) }
        "csa" -> if (contents.csa.isNotBlank()) CsaBox(contents.csa, cqw)
        else -> {
            if (isImageSlot(name)) return
            val values = slotValues(name, contents).take(limit)
            if (values.isEmpty()) {
                Logger.d("COH", "hero: no values for slot '$name', skipped")
            } else {
                MetaText(values.joinToString(", "), cqw)
            }
        }
    }
}

// Inline row of slot refs (e.g. the meta row): one Row, style per slot name.
@Composable
private fun MetaSlotRow(
    refs: List<Pair<String, Int>>,
    contents: CoherenceProgramContents,
    gap: Dp,
    cqw: Dp = 0.dp
) {
    if (refs.all { (name, _) -> slotValues(name, contents).isEmpty() }) {
        Logger.d("COH", "hero: meta row empty, skipped")
        return
    }
    Row(
        horizontalArrangement = Arrangement.spacedBy(gap),
        verticalAlignment = Alignment.CenterVertically
    ) {
        refs.forEach { (name, maxItems) ->
            val limit = maxItems.takeIf { it > 0 } ?: Int.MAX_VALUE
            when (name) {
                "year", "duration" -> slotValues(name, contents).forEach { MetaText(it, cqw) }
                "genres" -> contents.genres.take(limit).forEach { GenreChip(it, cqw) }
                "csa" -> if (contents.csa.isNotBlank()) CsaBox(contents.csa, cqw)
                "title" -> if (contents.title.isNotBlank()) HeroTitle(contents.title, cqw)
                "synopsis" -> if (contents.synopsis.isNotBlank()) HeroBody(contents.synopsis, 3, cqw)
                else -> {
                    val values = slotValues(name, contents).take(limit)
                    if (values.isNotEmpty()) MetaText(values.joinToString(", "), cqw)
                }
            }
        }
    }
}

@Composable
private fun HeroTitle(text: String, cqw: Dp = 0.dp) {
    Text(
        text = text,
        color = textPrimary(),
        fontSize = dpToSp(scaleDp(15, 3.4f, cqw)),
        fontWeight = FontWeight.SemiBold,
        letterSpacing = (-0.02f).sp,
        maxLines = 2,
        overflow = TextOverflow.Ellipsis
    )
}

@Composable
private fun MetaText(text: String, cqw: Dp = 0.dp) {
    Text(
        text = text,
        color = textMuted(),
        fontSize = dpToSp(scaleDp(7, 1.15f, cqw))
    )
}

@Composable
private fun HeroBody(text: String, maxLines: Int, cqw: Dp = 0.dp) {
    Text(
        text = text,
        color = textBody(),
        fontSize = dpToSp(scaleDp(8, 1.35f, cqw)),
        // CSS .lc-body { max-width: 52ch } — ch is the "0" advance,
        // ≈0.606em of the body font (itself 1.35cqw) → ≈42.5cqw.
        modifier = Modifier.widthIn(
            max = (cqw * 52f * 0.606f * 1.35f).coerceAtLeast(220.dp)
        ),
        maxLines = maxLines,
        overflow = TextOverflow.Ellipsis
    )
}

@Composable
private fun RailsRegion(
    region: CoherenceRegion,
    contents: CoherenceProgramContents,
    modifier: Modifier = Modifier,
    onRailItemClicked: ((Int) -> Unit)? = null,
    isTV: Boolean = false,
    cqw: Dp = 0.dp,
    railHeight: Dp = 0.dp,
    railNames: List<String>,
    posterRequester: FocusRequester,
    actionRequester: FocusRequester?,
    railEntryRequesters: List<FocusRequester>,
    railStripBase: Int = 2,
    onStripFocused: (Int) -> Unit = {},
    onStripBounds: (Int, Int, Int) -> Unit = { _, _, _ -> }
) {
    val templateRailDefs = region.rails.associateBy { it.name }

    // Vertical navigation between rails targets the card that was focused
    // last in the destination rail (0 = first card until the user moves
    // within it). Card 0 of every rail keeps the strip-entry requester
    // supplied by the host view so cross-strip targets (hero, actions)
    // still work; requesters for the remaining cards live here.
    val railCardRequesters = remember(contents) {
        railNames.map { railName ->
            val items = contents.rails[railName]?.items ?: emptyList()
            val maxItems = templateRailDefs[railName]?.max?.takeIf { it > 0 } ?: 20
            List(minOf(items.size, maxItems)) { FocusRequester() }
        }
    }
    val railFocusMemory = remember(contents) { MutableList(railNames.size) { 0 } }

    // Requester for card `index` of rail `seq`.
    fun cardRequester(seq: Int, index: Int): FocusRequester =
        if (index == 0) railEntryRequesters[seq] else railCardRequesters[seq][index]

    // One rbody around all rails: 8px inset, 8dp gap between rails.
    // The KDL region height assumed a single rail — with several rails each
    // one sizes to its content (title + card row) instead of claiming the
    // whole region height, which doubled the space between rails.
    Column(
        modifier = modifier
            .fillMaxWidth()
            .heightIn(min = railHeight)
            .padding(8.dp)
    ) {
        railNames.forEachIndexed { railSeq, railName ->
            val railItems = contents.rails[railName]?.items ?: return@forEachIndexed
            val def = templateRailDefs[railName]
            val title = def?.title?.takeIf { it.isNotBlank() && it.toIntOrNull() == null }
                ?.ifBlank { null }
                ?: CoherenceThemeState.labels.rails[railName]
                ?: railName.replace("-", " ").replaceFirstChar(Char::uppercaseChar)

            val railStripId = railStripBase + railSeq
            val maxItems = def?.max?.takeIf { it > 0 } ?: 20
            Logger.d(
                "COH",
                "render rail#$railSeq '$railName' items=${railItems.size} max=$maxItems " +
                    "strip=$railStripId h=$railHeight def=${def?.let { "title='${it.title}' max=${it.max}" } ?: "none"}"
            )

            if (railSeq > 0) Spacer(Modifier.height(8.dp))

            // The whole rail block (title + cards) is one focus strip: report its
            // bounds so the view can scroll it fully into view when the strip
            // gets focus.
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .onFocusChanged { state -> if (state.hasFocus) onStripFocused(railStripId) }
                    .onGloballyPositioned { coords ->
                        val top = coords.positionInRoot().y.toInt()
                        onStripBounds(railStripId, top, top + coords.size.height)
                    }
            ) {
                Text(
                    text = title,
                    color = textPrimary(),
                    fontSize = dpToSp(scaleDp(7, 1.15f, cqw)),
                    fontWeight = FontWeight.Medium,
                    modifier = Modifier.fillMaxWidth().padding(bottom = 4.dp)
                )

                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .horizontalScroll(rememberScrollState()),
                    horizontalArrangement = Arrangement.spacedBy(6.dp)
                ) {
                    railItems.take(maxItems).forEachIndexed { i, item ->
                        CoherenceRailCard(
                            item = item,
                            onClick = { item.programId?.toIntOrNull()?.let { onRailItemClicked?.invoke(it) } },
                            isTV = isTV,
                            cqw = cqw,
                            requester = cardRequester(railSeq, i),
                            onCardFocused = { railFocusMemory[railSeq] = i },
                            upTarget = {
                                if (railSeq == 0) (actionRequester ?: posterRequester)
                                else cardRequester(railSeq - 1, railFocusMemory[railSeq - 1])
                            },
                            downTarget = {
                                val next = railSeq + 1
                                if (next < railCardRequesters.size) {
                                    cardRequester(next, railFocusMemory[next])
                                } else {
                                    null
                                }
                            }
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun CoherenceRailCard(
    item: RailItem,
    onClick: () -> Unit,
    isTV: Boolean = false,
    cqw: Dp = 0.dp,
    requester: FocusRequester,
    onCardFocused: () -> Unit = {},
    upTarget: () -> FocusRequester?,
    downTarget: () -> FocusRequester?
) {
    val cardWidth = scaleDp(28, 5.4f, cqw)

    // The one item renderer owns the card chrome, the TV focus border and the
    // focus chain; this region keeps only the Coherence-specific content.
    RailItemView(
        modifier = Modifier.width(cardWidth),
        onItemClick = onClick,
        isTV = isTV,
        requester = requester,
        upTarget = upTarget,
        downTarget = downTarget,
        onFocused = { onCardFocused() }
    ) {
        Column {
            // .lc-card .th — poster: 2/3 aspect, 3px radius, hairline border, cover.
            AsyncImage(
                model = remoteImageUrl(item.content),
                contentDescription = item.label,
                contentScale = ContentScale.Crop,
                modifier = Modifier
                    .fillMaxWidth()
                    .background(
                        Brush.linearGradient(
                            listOf(Color(0xFF3B4250), Color(0xFF171B21)),
                            start = Offset.Zero,
                            end = Offset.Infinite
                        )
                    )
                    .aspectRatio(2f / 3f)
                    .clip(RoundedCornerShape(3.dp))
                    .border(1.dp, Color(0x24FFFFFF), RoundedCornerShape(3.dp))
            )

            // .lc-card .ct — title: 3px top margin only, ellipsis, no side insets.
            Text(
                text = item.label.orEmpty(),
                color = textMuted(),
                fontSize = dpToSp(scaleDp(5, 0.85f, cqw)),
                modifier = Modifier.padding(top = 3.dp),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
    }
}

@Composable
private fun CoherenceActionButton(
    label: String,
    emphasis: String,
    bgColorOverride: String,
    onClick: () -> Unit,
    isTV: Boolean,
    cqw: Dp = 0.dp,
    requester: FocusRequester,
    upTarget: FocusRequester? = null,
    downTarget: FocusRequester? = null
) {
    val isFocused = remember { mutableStateOf(false) }
    val theme = CoherenceThemeState.theme

    val primaryBg = parseHexColorOrNull(theme.variants.actionPrimary.bgColor) ?: Color(0xFFF247F5)
    val normalBg = parseHexColorOrNull(theme.kinds.action.bgColor) ?: Color(0xFFF247F5)
    val primaryText = parseHexColorOrNull(theme.variants.actionPrimary.textColor) ?: Color(0xFF111111)
    val normalText = parseHexColorOrNull(theme.kinds.action.textColor) ?: Color(0xFFFFFFFF)

    // Live template (rev 13) defines per-action `format bg-color`
    // (play #12F373, trailer #FF0505, wishlist #1B288D); fall back to
    // theme defaults when a template has no format block.
    val base: Color = when (emphasis) {
        "primary" -> parseHexColorOrNull(bgColorOverride) ?: primaryBg
        else -> parseHexColorOrNull(bgColorOverride) ?: normalBg
    }
    // Template (.lc-btn) keeps the background constant; focus is shown by
    // the white outline only — no brightening.

    // focusProperties must precede the focus target (Button's clickable) in the chain.
    // Up goes to the poster, down to the first rail (left unset when there is none).
    val buttonModifier = Modifier
        .focusRequester(requester)
        .onFocusChanged { state -> isFocused.value = state.isFocused }
        .focusProperties {
            if (upTarget != null) up = upTarget
            if (downTarget != null) down = downTarget
        }

    Button(
        onClick = onClick,
        modifier = buttonModifier.widthIn(min = 72.dp),
        colors = ButtonDefaults.buttonColors(
            containerColor = base,
            disabledContainerColor = base
        ),
        shape = RoundedCornerShape(4.dp),
        // Template .lc-btn always has a hairline border (white for primary,
        // rgba white .35 otherwise); TV focus upgrades it to a 3dp outline.
        // Drawn by the Surface so it isn't painted over by the background.
        border = if (isTV && isFocused.value) BorderStroke(3.dp, Color.White)
            else if (emphasis == "primary") BorderStroke(1.dp, Color.White)
            else BorderStroke(1.dp, Color.White.copy(alpha = 0.35f)),
        contentPadding = PaddingValues(vertical = 4.dp, horizontal = 9.dp),
        elevation = ButtonDefaults.buttonElevation(defaultElevation = 0.dp, pressedElevation = 0.dp)
    ) {
        Text(
            text = label,
            // CSS .lc-btn.primary { color: #111 } — dark label on the primary bg.
            color = if (emphasis == "primary") primaryText else normalText,
            fontSize = dpToSp(scaleDp(8, 1.25f, cqw)),
            fontWeight = if (emphasis == "primary") FontWeight.SemiBold else FontWeight.Medium
        )
    }
}

@Composable
private fun ActionsSection(
    actions: List<CoherenceActionDef>,
    modifier: Modifier = Modifier,
    onActionClicked: ((String) -> Unit)? = null,
    isTV: Boolean = false,
    cqw: Dp = 0.dp,
    posterRequester: FocusRequester,
    actionRequesters: List<FocusRequester>,
    firstRailEntry: FocusRequester?,
    onStripFocused: (Int) -> Unit = {},
    onStripBounds: (Int, Int, Int) -> Unit = { _, _, _ -> }
) {
    Row(
        modifier = modifier
            .padding(horizontal = 26.dp, vertical = 12.dp)
            .onFocusChanged { state -> if (state.hasFocus) onStripFocused(1) }
            .onGloballyPositioned { coords ->
                val top = coords.positionInRoot().y.toInt()
                onStripBounds(1, top, top + coords.size.height)
            },
        horizontalArrangement = Arrangement.spacedBy(12.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        actions.forEachIndexed { i, action ->
            CoherenceActionButton(
                label = actionLabel(action.name),
                emphasis = action.emphasis,
                bgColorOverride = action.bgColor,
                onClick = { onActionClicked?.invoke(action.name) },
                isTV = isTV,
                cqw = cqw,
                requester = actionRequesters[i],
                upTarget = posterRequester,
                downTarget = firstRailEntry
            )
        }
    }
}

@Composable
private fun GenreChip(text: String, cqw: Dp = 0.dp) {
    Text(
        text = text,
        color = textPrimary(),
        fontSize = dpToSp(scaleDp(7, 1.1f, cqw)),
        modifier = Modifier
            .border(1.dp, Color(0x4DFFFFFF), RoundedCornerShape(99.dp))
            .padding(vertical = 2.dp, horizontal = 8.dp)
    )
}

@Composable
private fun CsaBox(text: String, cqw: Dp = 0.dp) {
    Text(
        text = text,
        color = textPrimary(),
        fontSize = dpToSp(scaleDp(7, 1.05f, cqw)),
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier
            .border(1.dp, Color(0x73FFFFFF), RoundedCornerShape(3.dp))
            .padding(vertical = 1.dp, horizontal = 4.dp)
    )
}

@Composable
private fun TemplateRegion(
    region: CoherenceRegion,
    modifier: Modifier = Modifier
) {
    Box(modifier = modifier.background(Color(0xFF141A22))) {
        Text(
            text = "Region: ${region.name}",
            color = textMuted(),
            fontSize = 12.sp,
            modifier = Modifier.padding(12.dp)
        )
    }
}

// ---- Helpers ----

private fun hasFillBackdrop(region: CoherenceRegion): Boolean =
    region.layers.any { layer ->
        layer.slots.any { it.anchor == "fill" || it.name == "backdrop" }
    }

private fun regionActions(region: CoherenceRegion): List<CoherenceActionDef> =
    region.layers.flatMap { layer ->
        layer.rows.flatMap { it.actions } +
            layer.columns.flatMap { col -> col.rows.flatMap { it.actions } }
    }

private fun actionAvailable(name: String, contents: CoherenceProgramContents): Boolean = when (name) {
    "play" -> contents.actions.play
    "trailer" -> contents.actions.trailer
    "wishlist" -> contents.actions.wishlist
    else -> true
}

private fun actionLabel(name: String): String =
    CoherenceThemeState.labels.actions[name] ?: name.replace("-", " ").replaceFirstChar(Char::uppercaseChar)

private fun slotValues(name: String, contents: CoherenceProgramContents): List<String> = when (name) {
    "director" -> contents.director
    "casting" -> contents.casting
    "year" -> listOf(contents.year).filter { it.isNotBlank() }
    "duration" -> listOf(contents.duration).filter { it.isNotBlank() }
    "csa" -> listOf(contents.csa).filter { it.isNotBlank() }
    "title" -> listOf(contents.title).filter { it.isNotBlank() }
    "original_title" -> listOf(contents.originalTitle).filter { it.isNotBlank() }
    "synopsis" -> listOf(contents.synopsis).filter { it.isNotBlank() }
    "genres" -> contents.genres
    // Image-backed (backdrop/poster) and unknown slots have no text values.
    else -> emptyList()
}

private fun isImageSlot(name: String): Boolean = name == "backdrop" || name == "poster"

private fun slotLabel(slot: CoherenceSlot): String =
    slot.attrs["label"]?.ifBlank { null }
        ?: CoherenceThemeState.labels.slots[slot.name]
        ?: when (slot.name) {
            "director" -> "Réalisation"
            "casting" -> "Avec"
            else -> slot.name.replace("-", " ").replaceFirstChar(Char::uppercaseChar)
        }

/** Percent is a share of the visible viewport (not a weight). A height without `%` is pixels. */
private fun parseHeight(height: String, viewportHeight: Dp): Dp {
    val trimmed = height.trim()
    if (trimmed.endsWith("%")) {
        val pct = trimmed.removeSuffix("%").toFloatOrNull() ?: 58f
        return viewportHeight * (pct / 100f)
    }
    return trimmed.toIntOrNull()?.dp ?: (viewportHeight * 0.28f)
}

/** Scales with the viewport (cqw = 1% of container width, in dp), floored at
 * minPx so content stays readable on small screens. Deliberately uncapped:
 * the KDL layout is proportional, so sizes must keep adapting on ANY viewport. */
private fun scaleDp(minPx: Int, cqwMult: Float, cqw: Dp): Dp =
    maxOf(minPx.dp, cqw * cqwMult)

private fun gapDp(gap: String): Dp = when (gap.trim()) {
    "sm" -> 6.dp
    "md" -> 12.dp
    "lg" -> 24.dp
    else -> 0.dp
}

private fun dpToSp(dp: Dp): TextUnit = dp.value.sp

internal fun parseHexColorOrNull(hex: String): Color? {
    val clean = hex.trim().removePrefix("#")
    val argb = when (clean.length) {
        6 -> "FF$clean"
        8 -> clean
        else -> return null
    }
    return runCatching { Color(argb.toLong(16)) }.getOrNull()
}

private fun brighten(color: Color, amount: Float = 0.35f): Color {
    return Color(
        red = color.red * (1 - amount) + 1.0f * amount,
        green = color.green * (1 - amount) + 1.0f * amount,
        blue = color.blue * (1 - amount) + 1.0f * amount,
        alpha = color.alpha * (1 - amount) + 1.0f * amount
    )
}
